import { useDeferredValue, useState } from 'react';
import { Eye, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@components/common/EmptyState';
import { PageHeader } from '@components/common/PageHeader';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import {
  useApolloLeadImportCommit,
  useApolloLists,
  useApolloLeadImportPreview,
  useApolloPeopleSearchFilterOptions,
  useApolloPeopleSearch,
  useApolloPersonEnrichment,
  useUsers,
} from '@hooks/useCRM';
import type {
  CRMApolloLeadImportCandidate,
  CRMApolloLeadImportPreviewResponse,
  CRMApolloPersonSearchResult,
  CRMApolloProspectEnrichmentResponse,
} from '@/types/api';

type ApolloSearchFormState = {
  q_keywords: string;
  apollo_list_id: string;
  person_titles: string;
  person_seniorities: string;
  person_locations: string;
  organization_locations: string;
  organization_domains: string;
  organization_num_employees_ranges: string;
  contact_email_status: string;
};

type ApolloImportDefaults = {
  owner_user_id: string;
  priority: string;
  status: string;
};

const EMPTY_APOLLO_SEARCH_FORM: ApolloSearchFormState = {
  q_keywords: '',
  apollo_list_id: '',
  person_titles: '',
  person_seniorities: '',
  person_locations: '',
  organization_locations: '',
  organization_domains: '',
  organization_num_employees_ranges: '',
  contact_email_status: '',
};

const EMPTY_APOLLO_IMPORT_DEFAULTS: ApolloImportDefaults = {
  owner_user_id: '',
  priority: 'medium',
  status: 'new',
};

const LEAD_IMPORT_STATUS_OPTIONS = [
  { id: 'new', label: 'New' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'called', label: 'Called' },
  { id: 'interested', label: 'Interested' },
];

const PRIORITY_OPTIONS = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

function splitCommaInput(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function splitSemicolonInput(value: string) {
  return value
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
}

function appendDelimitedValue(current: string, value: string, delimiter: ',' | ';') {
  const normalized = value.trim();
  if (!normalized) {
    return current;
  }

  const existingValues =
    delimiter === ','
      ? splitCommaInput(current).map((item) => item.toLowerCase())
      : splitSemicolonInput(current).map((item) => item.toLowerCase());
  if (existingValues.includes(normalized.toLowerCase())) {
    return current;
  }

  const suffix = delimiter === ',' ? ', ' : '; ';
  if (!current.trim()) {
    return normalized;
  }
  return `${current.trim()}${suffix}${normalized}`;
}

function formatApolloValue(value: unknown) {
  if (value == null) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value, null, 2);
}

function buildApolloImportCandidates(
  results: CRMApolloPersonSearchResult[],
  selectedIds: Record<string, boolean>,
  defaults: ApolloImportDefaults,
): CRMApolloLeadImportCandidate[] {
  return results
    .filter((result) => selectedIds[result.person_id])
    .map((result) => ({
      ...result,
      owner_user_id: defaults.owner_user_id || null,
      priority: defaults.priority,
      status: defaults.status,
      source_type: 'apollo_search',
    }));
}

export function ApolloProspectingPage() {
  const navigate = useNavigate();
  const [apolloSearchForm, setApolloSearchForm] = useState<ApolloSearchFormState>(EMPTY_APOLLO_SEARCH_FORM);
  const [apolloImportDefaults, setApolloImportDefaults] = useState<ApolloImportDefaults>(EMPTY_APOLLO_IMPORT_DEFAULTS);
  const [selectedApolloIds, setSelectedApolloIds] = useState<Record<string, boolean>>({});
  const [selectedApolloPersonId, setSelectedApolloPersonId] = useState<string | null>(null);
  const [apolloInspectorOpen, setApolloInspectorOpen] = useState(false);
  const apolloSearchMutation = useApolloPeopleSearch();
  const apolloEnrichmentMutation = useApolloPersonEnrichment();
  const apolloPreviewMutation = useApolloLeadImportPreview();
  const apolloCommitMutation = useApolloLeadImportCommit();
  const { data: users } = useUsers();
  const { data: apolloLists, error: apolloListsError } = useApolloLists();
  const deferredKeywords = useDeferredValue(apolloSearchForm.q_keywords.trim());
  const {
    data: apolloFilterOptions,
    isLoading: isApolloFilterOptionsLoading,
    error: apolloFilterOptionsError,
  } = useApolloPeopleSearchFilterOptions(deferredKeywords || undefined, true);

  async function handleApolloSearch() {
    apolloPreviewMutation.reset();
    setSelectedApolloPersonId(null);
    setApolloInspectorOpen(false);
    await apolloSearchMutation.mutateAsync({
      q_keywords: apolloSearchForm.q_keywords || null,
      contact_label_ids: apolloSearchForm.apollo_list_id ? [apolloSearchForm.apollo_list_id] : [],
      person_titles: splitCommaInput(apolloSearchForm.person_titles),
      person_seniorities: splitCommaInput(apolloSearchForm.person_seniorities),
      person_locations: splitCommaInput(apolloSearchForm.person_locations),
      organization_locations: splitCommaInput(apolloSearchForm.organization_locations),
      organization_domains: splitCommaInput(apolloSearchForm.organization_domains),
      organization_num_employees_ranges: splitSemicolonInput(apolloSearchForm.organization_num_employees_ranges),
      contact_email_status: splitCommaInput(apolloSearchForm.contact_email_status),
      page: 1,
      per_page: 25,
    });
    setSelectedApolloIds({});
  }

  function toggleApolloSelection(personId: string) {
    setSelectedApolloIds((current) => ({
      ...current,
      [personId]: !current[personId],
    }));
  }

  async function handleApolloInspect(personId: string) {
    const selectedResult = apolloResults.find((result) => result.person_id === personId);
    setSelectedApolloPersonId(personId);
    setApolloInspectorOpen(true);
    if (!selectedResult) {
      return;
    }

    await apolloEnrichmentMutation.mutateAsync({
      person_id: personId,
      organization_domain: selectedResult.organization_domain ?? null,
    });
  }

  async function handleApolloPreview() {
    const candidates = buildApolloImportCandidates(
      apolloSearchMutation.data?.items ?? [],
      selectedApolloIds,
      apolloImportDefaults,
    );
    await apolloPreviewMutation.mutateAsync({ candidates });
  }

  async function handleApolloCommit() {
    const candidates = buildApolloImportCandidates(
      apolloSearchMutation.data?.items ?? [],
      selectedApolloIds,
      apolloImportDefaults,
    );
    const response = await apolloCommitMutation.mutateAsync({ candidates });
    if (response.leads[0]) {
      navigate(`/leads/${response.leads[0].id}`);
    }
    setSelectedApolloIds({});
  }

  const apolloResults = apolloSearchMutation.data?.items ?? [];
  const selectedApolloCandidates = buildApolloImportCandidates(apolloResults, selectedApolloIds, apolloImportDefaults);
  const selectedApolloPerson = apolloResults.find((result) => result.person_id === selectedApolloPersonId) ?? null;
  const selectedApolloEnrichment =
    apolloEnrichmentMutation.data?.person.person_id === selectedApolloPerson?.person_id
      ? apolloEnrichmentMutation.data
      : null;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Apollo Prospecting"
        subtitle="Search Apollo for net-new people, inspect a full record, and import selected prospects into CRM leads."
      />

      <SectionCard title="Apollo Search">
      <div className="import-panel">
        <p className="muted-copy">
          {isApolloFilterOptionsLoading
            ? 'Loading Apollo filter options...'
            : `Filter options sampled from ${apolloFilterOptions?.sample_count ?? 0} Apollo contact(s).`}
        </p>
        {apolloFilterOptionsError instanceof Error ? <p className="form-error">{apolloFilterOptionsError.message}</p> : null}

        <div className="form-grid">
          <label className="form-field form-field-wide">
            <span>Keywords</span>
            <input
              value={apolloSearchForm.q_keywords}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, q_keywords: event.target.value }))}
              placeholder="finance automation, controller, AP manager"
            />
          </label>
          <label className="form-field form-field-wide">
            <span>Apollo Predefined List</span>
            <SearchableSelect
              options={[
                { id: '', label: 'No list filter' },
                ...(apolloLists ?? []).map((list) => ({ id: list.id, label: list.name })),
              ]}
              value={apolloSearchForm.apollo_list_id}
              onChange={(value) => setApolloSearchForm((current) => ({ ...current, apollo_list_id: value }))}
              placeholder="No list filter"
              clearable={false}
            />
            {apolloListsError instanceof Error ? <p className="form-error">{apolloListsError.message}</p> : null}
          </label>
          <label className="form-field">
            <span>Titles</span>
            <input
              list="apollo-person-title-options"
              value={apolloSearchForm.person_titles}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, person_titles: event.target.value }))}
              placeholder="controller, finance manager"
            />
            <QuickPickRow
              options={(apolloFilterOptions?.person_titles ?? []).slice(0, 8)}
              onPick={(value) =>
                setApolloSearchForm((current) => ({
                  ...current,
                  person_titles: appendDelimitedValue(current.person_titles, value, ','),
                }))
              }
            />
          </label>
          <label className="form-field">
            <span>Seniorities</span>
            <input
              list="apollo-person-seniority-options"
              value={apolloSearchForm.person_seniorities}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, person_seniorities: event.target.value }))}
              placeholder="manager, director, vp"
            />
            <QuickPickRow
              options={(apolloFilterOptions?.person_seniorities ?? []).slice(0, 8)}
              onPick={(value) =>
                setApolloSearchForm((current) => ({
                  ...current,
                  person_seniorities: appendDelimitedValue(current.person_seniorities, value, ','),
                }))
              }
            />
          </label>
          <label className="form-field">
            <span>People Locations</span>
            <input
              list="apollo-person-location-options"
              value={apolloSearchForm.person_locations}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, person_locations: event.target.value }))}
              placeholder="new york, texas, sweden"
            />
            <QuickPickRow
              options={(apolloFilterOptions?.person_locations ?? []).slice(0, 8)}
              onPick={(value) =>
                setApolloSearchForm((current) => ({
                  ...current,
                  person_locations: appendDelimitedValue(current.person_locations, value, ','),
                }))
              }
            />
          </label>
          <label className="form-field">
            <span>HQ Locations</span>
            <input
              list="apollo-organization-location-options"
              value={apolloSearchForm.organization_locations}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, organization_locations: event.target.value }))}
              placeholder="california, chicago"
            />
            <QuickPickRow
              options={(apolloFilterOptions?.organization_locations ?? []).slice(0, 8)}
              onPick={(value) =>
                setApolloSearchForm((current) => ({
                  ...current,
                  organization_locations: appendDelimitedValue(current.organization_locations, value, ','),
                }))
              }
            />
          </label>
          <label className="form-field">
            <span>Company Domains</span>
            <input
              value={apolloSearchForm.organization_domains}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, organization_domains: event.target.value }))}
              placeholder="acme.com, example.com"
            />
          </label>
          <label className="form-field">
            <span>Employee Ranges</span>
            <input
              list="apollo-organization-employee-range-options"
              value={apolloSearchForm.organization_num_employees_ranges}
              onChange={(event) =>
                setApolloSearchForm((current) => ({ ...current, organization_num_employees_ranges: event.target.value }))
              }
              placeholder="1,10; 11,50; 51,200"
            />
            <QuickPickRow
              options={(apolloFilterOptions?.organization_num_employees_ranges ?? []).slice(0, 8)}
              onPick={(value) =>
                setApolloSearchForm((current) => ({
                  ...current,
                  organization_num_employees_ranges: appendDelimitedValue(current.organization_num_employees_ranges, value, ';'),
                }))
              }
            />
          </label>
          <label className="form-field">
            <span>Email Status</span>
            <input
              list="apollo-contact-email-status-options"
              value={apolloSearchForm.contact_email_status}
              onChange={(event) => setApolloSearchForm((current) => ({ ...current, contact_email_status: event.target.value }))}
              placeholder="verified, unverified"
            />
            <QuickPickRow
              options={(apolloFilterOptions?.contact_email_status ?? []).slice(0, 8)}
              onPick={(value) =>
                setApolloSearchForm((current) => ({
                  ...current,
                  contact_email_status: appendDelimitedValue(current.contact_email_status, value, ','),
                }))
              }
            />
          </label>
        </div>

        <datalist id="apollo-person-title-options">
          {(apolloFilterOptions?.person_titles ?? []).map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <datalist id="apollo-person-seniority-options">
          {(apolloFilterOptions?.person_seniorities ?? []).map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <datalist id="apollo-person-location-options">
          {(apolloFilterOptions?.person_locations ?? []).map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <datalist id="apollo-organization-location-options">
          {(apolloFilterOptions?.organization_locations ?? []).map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <datalist id="apollo-organization-employee-range-options">
          {(apolloFilterOptions?.organization_num_employees_ranges ?? []).map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <datalist id="apollo-contact-email-status-options">
          {(apolloFilterOptions?.contact_email_status ?? []).map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>

        <div className="form-actions">
          <button type="button" className="button button-primary" onClick={() => void handleApolloSearch()} disabled={apolloSearchMutation.isPending}>
            <Search size={16} />
            {apolloSearchMutation.isPending ? 'Searching Apollo…' : 'Search Apollo'}
          </button>
        </div>

        {apolloSearchMutation.error ? (
          <p className="form-error">
            {apolloSearchMutation.error instanceof Error ? apolloSearchMutation.error.message : 'Apollo search failed.'}
          </p>
        ) : null}

        {apolloSearchMutation.isSuccess && !apolloResults.length ? (
          <div className="empty-state">
            <h3>No Apollo prospects found</h3>
            <p>Try broader filters or remove a few constraints, then search again.</p>
          </div>
        ) : null}

        {apolloResults.length ? (
          <div className="detail-stack">
            <div className="subsection-header">
              <h3>Import Defaults</h3>
              <p>
                {apolloSearchMutation.data?.total_count != null
                  ? `${apolloSearchMutation.data.total_count} Apollo result(s) matched.`
                  : `${apolloResults.length} Apollo result(s) returned.`}
              </p>
            </div>

            <div className="form-grid">
              <label className="form-field">
                <span>Owner</span>
                <SearchableSelect
                  options={[
                    { id: '', label: 'Unassigned' },
                    ...(users ?? []).map((user) => ({
                      id: user.id,
                      label: `${user.first_name} ${user.last_name}`.trim() || user.email,
                      description: user.email,
                    })),
                  ]}
                  value={apolloImportDefaults.owner_user_id}
                  onChange={(value) => setApolloImportDefaults((current) => ({ ...current, owner_user_id: value }))}
                  placeholder="Unassigned"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Status</span>
                <SearchableSelect
                  options={LEAD_IMPORT_STATUS_OPTIONS}
                  value={apolloImportDefaults.status}
                  onChange={(value) => setApolloImportDefaults((current) => ({ ...current, status: value }))}
                  placeholder="Status"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Priority</span>
                <SearchableSelect
                  options={PRIORITY_OPTIONS}
                  value={apolloImportDefaults.priority}
                  onChange={(value) => setApolloImportDefaults((current) => ({ ...current, priority: value }))}
                  placeholder="Priority"
                  clearable={false}
                />
              </label>
            </div>

            <div className="table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Person</th>
                    <th>Company</th>
                    <th>Location</th>
                    <th>Email Status</th>
                    <th>Inspect</th>
                  </tr>
                </thead>
                <tbody>
                  {apolloResults.map((result) => (
                    <tr key={result.person_id} className={selectedApolloPersonId === result.person_id ? 'is-selected' : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(selectedApolloIds[result.person_id])}
                          onChange={() => toggleApolloSelection(result.person_id)}
                        />
                      </td>
                      <td>
                        <strong>{result.full_name || [result.first_name, result.last_name].filter(Boolean).join(' ') || 'Unnamed prospect'}</strong>
                        <span>{result.title || result.linkedin_url || 'No title available'}</span>
                      </td>
                      <td>
                        <strong>{result.organization_name || 'Unknown company'}</strong>
                        <span>{result.organization_domain || 'No company domain'}</span>
                      </td>
                      <td>{[result.city, result.state, result.country].filter(Boolean).join(', ') || '—'}</td>
                      <td>{result.email_status || 'Unknown'}</td>
                      <td>
                        <button
                          type="button"
                          className="button button-secondary apollo-inspect-button"
                          onClick={() => void handleApolloInspect(result.person_id)}
                        >
                          <Eye size={16} />
                          Inspect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="button button-secondary"
                onClick={() => void handleApolloPreview()}
                disabled={!selectedApolloCandidates.length || apolloPreviewMutation.isPending}
              >
                {apolloPreviewMutation.isPending ? 'Previewing…' : `Preview ${selectedApolloCandidates.length} Selection(s)`}
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={() => void handleApolloCommit()}
                disabled={!selectedApolloCandidates.length || apolloCommitMutation.isPending}
              >
                {apolloCommitMutation.isPending ? 'Importing…' : 'Commit Apollo Import'}
              </button>
            </div>

            {apolloPreviewMutation.error ? (
              <p className="form-error">
                {apolloPreviewMutation.error instanceof Error ? apolloPreviewMutation.error.message : 'Apollo import preview failed.'}
              </p>
            ) : null}

            {apolloCommitMutation.error ? (
              <p className="form-error">
                {apolloCommitMutation.error instanceof Error ? apolloCommitMutation.error.message : 'Apollo import commit failed.'}
              </p>
            ) : null}

            {apolloPreviewMutation.data ? <ApolloImportPreview preview={apolloPreviewMutation.data} /> : null}
            {apolloInspectorOpen && selectedApolloPerson ? (
              <ApolloProspectInspector
                prospect={selectedApolloPerson}
                enrichment={selectedApolloEnrichment}
                isLoading={apolloEnrichmentMutation.isPending}
                error={apolloEnrichmentMutation.error}
                onRefresh={() => void handleApolloInspect(selectedApolloPerson.person_id)}
                onClose={() => {
                  setApolloInspectorOpen(false);
                  setSelectedApolloPersonId(null);
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      </SectionCard>
    </div>
  );
}

type QuickPickRowProps = {
  options: string[];
  onPick: (value: string) => void;
};

function QuickPickRow({ options, onPick }: QuickPickRowProps) {
  if (!options.length) {
    return null;
  }

  return (
    <div className="toolbar-cluster">
      {options.map((option) => (
        <button key={option} type="button" className="pill-button" onClick={() => onPick(option)}>
          {option}
        </button>
      ))}
    </div>
  );
}

type ApolloImportPreviewProps = {
  preview: CRMApolloLeadImportPreviewResponse;
};

function ApolloImportPreview({ preview }: ApolloImportPreviewProps) {
  return (
    <div className="import-preview">
      <div className="subsection-header">
        <h3>Apollo Preview</h3>
        <p>
          {preview.create_count} lead(s) will be created and {preview.link_count} selection(s) will link to an existing CRM lead.
        </p>
      </div>

      <div className="table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Prospect</th>
              <th>Company</th>
              <th>Decision</th>
              <th>Matches</th>
              <th>Warnings</th>
            </tr>
          </thead>
          <tbody>
            {preview.items.map((item) => (
              <tr key={item.candidate.person_id}>
                <td>
                  <strong>{item.candidate.full_name || [item.candidate.first_name, item.candidate.last_name].filter(Boolean).join(' ') || 'Unnamed prospect'}</strong>
                  <span>{item.candidate.title || item.candidate.linkedin_url || item.candidate.person_id}</span>
                </td>
                <td>{item.candidate.organization_name || item.candidate.organization_domain || '—'}</td>
                <td>{item.decision.replace(/_/g, ' ')}</td>
                <td>
                  {[
                    item.matched_lead_id ? `lead ${item.matched_lead_id}` : null,
                    item.matched_contact_id ? `contact ${item.matched_contact_id}` : null,
                    item.matched_account_id ? `account ${item.matched_account_id}` : null,
                  ].filter(Boolean).join(' • ') || 'No existing CRM match'}
                </td>
                <td>{item.warnings.join(' ') || 'Ready'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type ApolloProspectInspectorProps = {
  prospect: CRMApolloPersonSearchResult;
  enrichment?: CRMApolloProspectEnrichmentResponse | null;
  isLoading: boolean;
  error: unknown;
  onRefresh: () => void;
  onClose: () => void;
};

function ApolloProspectInspector({ prospect, enrichment, isLoading, error, onRefresh, onClose }: ApolloProspectInspectorProps) {
  const person = enrichment?.person;
  const organization = enrichment?.organization;
  const normalizedFields = [
    ['Apollo Person ID', prospect.person_id],
    ['Name', prospect.full_name || [prospect.first_name, prospect.last_name].filter(Boolean).join(' ') || '—'],
    ['Title', prospect.title],
    ['Email', prospect.email],
    ['Email Status', prospect.email_status],
    ['Company', prospect.organization_name],
    ['Company Domain', prospect.organization_domain],
    ['Company ID', prospect.organization_id],
    ['LinkedIn', prospect.linkedin_url],
    ['Location', [prospect.city, prospect.state, prospect.country].filter(Boolean).join(', ') || '—'],
  ] as const;
  const enrichedFields = person
    ? [
        ['Apollo Person ID', person.person_id],
        ['Name', person.full_name || [person.first_name, person.last_name].filter(Boolean).join(' ') || '—'],
        ['Title', person.title],
        ['Email', person.email],
        ['LinkedIn', person.linkedin_url],
        ['Location', [person.city, person.state, person.country].filter(Boolean).join(', ') || '—'],
        ['Company', person.organization_name],
        ['Company Domain', person.organization_domain],
        ['Company ID', person.organization_id],
      ]
    : [];

  const enrichedOrganizationFields = organization
    ? [
        ['Organization ID', organization.organization_id],
        ['Name', organization.name],
        ['Domain', organization.domain],
        ['Website', organization.website_url],
        ['Industry', organization.industry],
        ['Employee Count', organization.employee_count],
        ['Revenue', organization.revenue],
        ['Phone', organization.phone],
        ['Location', [organization.city, organization.state, organization.country].filter(Boolean).join(', ') || '—'],
      ]
    : [];

  return (
    <div className="apollo-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="apollo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apollo-prospect-inspector-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="apollo-modal-header">
          <div className="subsection-header">
            <h3 id="apollo-prospect-inspector-title">Prospect Inspector</h3>
            <p>Inspect the prospect summary, then enrich it to load Apollo's actual person and company values.</p>
          </div>
          <button type="button" className="button button-secondary apollo-modal-close" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="apollo-inspector-actions">
          <button type="button" className="button button-secondary" onClick={onRefresh} disabled={isLoading}>
            {isLoading ? 'Loading full Apollo record…' : person ? 'Refresh full Apollo record' : 'Load full Apollo record'}
          </button>
          {error instanceof Error ? <p className="form-error">{error.message}</p> : null}
        </div>

        <div className="apollo-inspector-grid">
          <div className="apollo-inspector-section">
            <h4>Search Result</h4>
            <dl className="apollo-inspector-meta">
              {normalizedFields.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{formatApolloValue(value)}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="apollo-inspector-section">
            <h4>Enriched Person</h4>
            {person ? (
              <dl className="apollo-inspector-meta">
                {enrichedFields.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{formatApolloValue(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <div className="empty-state">
                <h3>No enriched person loaded yet</h3>
                <p>Click “Load full Apollo record” to fetch Apollo's actual fields.</p>
              </div>
            )}
          </div>
        </div>

        {organization ? (
          <div className="apollo-inspector-section">
            <h4>Enriched Organization</h4>
            <dl className="apollo-inspector-meta">
              {enrichedOrganizationFields.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{formatApolloValue(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </div>
    </div>
  );
}
